import React from 'react'
import RichHtml from './RichHtml'
import { ManifestGallery } from './Gallery'
import VideoEmbed, { extractYouTubeId } from './VideoEmbed'

/**
 * Renders migrated article HTML that may contain Joomla shortcodes:
 *   {gallery}folder/path,option=value{/gallery}  -> SIGE image gallery
 *   {source} ...raw (often entity-encoded) HTML... {/source} -> Sourcerer block,
 *      historically used to embed a YouTube <iframe>.
 *
 * The content is split on those shortcodes. HTML segments render via RichHtml
 * (preserving structure); {gallery} renders a ManifestGallery; {source} blocks
 * are scanned for a YouTube video and rendered as a safe VideoEmbed (we extract
 * the video id and emit our own locked-down iframe rather than trusting the
 * embedded HTML - RichHtml itself forbids iframes for safety).
 *
 * New posts can also just paste a YouTube URL on its own line, or use
 * {source}<iframe ... youtube ...>{/source}; both resolve to a VideoEmbed.
 */

// Match {gallery}...{/gallery} OR {source}...{/source} (non-greedy, case-insensitive).
// Factories return a FRESH stateful (global) regex per use so we never mutate a
// shared `lastIndex` (which the Rules of React forbid during render).
const shortcodeRe = () => /\{(gallery|source)\}([\s\S]*?)\{\/\1\}/gi

// A bare YouTube URL sitting in the text (outside any shortcode).
const ytUrlRe = () => /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?[^\s"'<]*v=[A-Za-z0-9_-]{11}|youtu\.be\/[A-Za-z0-9_-]{11}|youtube(?:-nocookie)?\.com\/embed\/[A-Za-z0-9_-]{11})/gi

function parseFolder(inner) {
  // inner looks like "flightlog/20260812-witchieknowe,turbo=1" or "cacrabank"
  return (inner || '').split(',')[0].trim()
}

// Render an HTML segment, but first pull out any bare YouTube URLs into embeds.
function renderHtmlSegment(html, keyBase) {
  if (!html || !html.trim()) return []
  const out = []
  let last = 0
  let m
  let i = 0
  const re = ytUrlRe()
  while ((m = re.exec(html)) !== null) {
    const id = extractYouTubeId(m[0])
    if (!id) continue
    const before = html.slice(last, m.index)
    if (before.trim()) out.push(<RichHtml key={`${keyBase}-h${i}`} html={before} />)
    out.push(<VideoEmbed key={`${keyBase}-v${i}`} youtubeId={id} />)
    last = m.index + m[0].length
    i += 1
  }
  const tail = html.slice(last)
  if (tail.trim()) out.push(<RichHtml key={`${keyBase}-h${i}`} html={tail} />)
  return out
}

function GalleryContent({ content }) {
  if (!content) return null

  // Fast path: no shortcodes and no YouTube URL -> render as-is.
  if (!/\{(gallery|source)\}/i.test(content) && !ytUrlRe().test(content)) {
    return <RichHtml html={content} />
  }

  const parts = []
  let lastIndex = 0
  let match
  let key = 0

  const re = shortcodeRe() // fresh stateful regex per render

  while ((match = re.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const htmlSegment = content.slice(lastIndex, match.index)
      parts.push(...renderHtmlSegment(htmlSegment, `seg-${key}`))
    }

    const kind = match[1].toLowerCase()
    const inner = match[2]

    if (kind === 'gallery') {
      const folder = parseFolder(inner)
      if (folder) {
        parts.push(<ManifestGallery key={`gallery-${key}`} folder={folder} columns={4} />)
      }
    } else if (kind === 'source') {
      // Sourcerer block: extract a YouTube id from the (often entity-encoded)
      // embedded HTML and render a safe embed. If there's no video, drop it
      // (the raw iframe HTML would be stripped by RichHtml anyway).
      const id = extractYouTubeId(inner)
      if (id) {
        parts.push(<VideoEmbed key={`source-${key}`} youtubeId={id} />)
      }
    }

    lastIndex = re.lastIndex
    key += 1
  }

  if (lastIndex < content.length) {
    const tail = content.slice(lastIndex)
    parts.push(...renderHtmlSegment(tail, `seg-${key}`))
  }

  return <>{parts}</>
}

export default GalleryContent
